import { useCallback, useEffect, useRef } from "react";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import {
  fetchConditionsAndSlotsAsync,
  selectAppState,
  setActivePage,
  setIFrameLink,
  setShowFlowBuilder,
  updateBreadcrumbValue,
} from "../app/slice/appSlice";
import {
  addVariant,
  selectCondition,
  updateVariantStatus,
  selectConditionAsync,
  setSelectedVariantId,
  setSelectedSlot,
} from "../app/slice/conditionSlice";
import { addSlot, removeSlot, updateSlot } from "../app/slice/slotSlice";
import { openMissingEntitiesModal } from "../app/slice/missingEntitiesSlice";
import { APP_PLATFORM, WIDGET_EDITOR_URL } from "../config";
import { openCrispChat, sendSupportMessage } from "../utils/crisp";
import { Platform, Slot } from "../models";
import {
  AIChatbotPostMessageEventType,
  generateBreadcrumbFormat,
  IFramePostMessageType,
  sendPostMessage,
} from "../utils/helpers";
import { PageType } from "@reconvert/reconvert-utils";
import { RootState, store } from "../app/store";
import { FunctionRegistry } from "../utils/functionRegistry";
import { getToken } from "../services/auth.service";
import { getVariantById } from "../services/variants.service";
import i18n from "../utils/i18n";
import useUnsavedBar from "./useUnsavedBar";
import type {
  ToolOutputsMessageEvent,
  UseAIChatContextReturn,
} from "../types/aiChatBot";
import { useFlowBuilderSession } from "@reconvert/react-ui-component";

/** Sends ACTION_ACK to the chatbot and clears the sessionStorage flag for the given action type. */
const sendAckIfNeeded = (actionType: string) => {
  if (sessionStorage.getItem(actionType)) {
    sendPostMessage(IFramePostMessageType.ACTION_ACK);
    sessionStorage.removeItem(actionType);
  }
};

/**
 * Centralized all AI Chatbot ↔ Flow Builder communication.
 *
 * Responsibilities:
 * 1. Sends context updates (current view, selected page) to the AI Chatbot via postMessage
 *    so it always knows the user's current position in the Flow Builder.
 * 2. Handles tool-output actions received from the AI Chatbot (via the parent iframe)
 *    such as navigating to a page/condition/variant, opening the widget editor,
 *    and mutating slots. All actions are gated by the unsaved-changes modal — if the
 *    user has pending edits, they must save or discard before the action proceeds.
 *
 * The hook exposes its handler via a ref (processToolOutputsActionsRef) so the
 * existing message listener in useAppInit can call it without adding a function
 * to the useEffect dependency array.
 */
const useAIChatContext = (): UseAIChatContextReturn => {
  const { activePage, showFlowBuilder } = useAppSelector(selectAppState);
  const dispatch = useAppDispatch();
  const { preventAction, unsavedChanges } = useUnsavedBar();
  const { setPageInSession, setSelectedConditionInSession, setSelectedVariantInSession } = useFlowBuilderSession();

  const skipLoadConditionsRef = useRef(false);
  const preventActionRef = useRef(preventAction);

  preventActionRef.current = preventAction;

  // --- Context sync: tell the AI Chatbot which view/page the user is on ---

  useEffect(() => {
    if (APP_PLATFORM === Platform.Shopify) {
      sendPostMessage(IFramePostMessageType.UPDATE_AI_CHAT_CONTEXT, {
        currentView: 'FLOW_BUILDER',
        eventType: AIChatbotPostMessageEventType.UPDATE_CURRENT_VIEW,
      });
    }
  }, []);

  useEffect(() => {
    if (APP_PLATFORM === Platform.Shopify && showFlowBuilder) {
      sendPostMessage(IFramePostMessageType.UPDATE_AI_CHAT_CONTEXT, { page: activePage, eventType: AIChatbotPostMessageEventType.SET_SELECTED_PAGE });
    } else if (APP_PLATFORM === Platform.Shopify && !showFlowBuilder) {
      sendPostMessage(IFramePostMessageType.UPDATE_AI_CHAT_CONTEXT, { page: undefined, eventType: AIChatbotPostMessageEventType.SET_SELECTED_PAGE });
    }
  }, [showFlowBuilder, activePage]);


  const hasUnsavedChanges = unsavedChanges.length > 0;

  useEffect(() => {
    if (APP_PLATFORM === Platform.Shopify) {
      sendPostMessage(IFramePostMessageType.UPDATE_AI_CHAT_CONTEXT, {
        hasUnsavedChanges,
        eventType: AIChatbotPostMessageEventType.UPDATE_HAS_UNSAVED_CHANGES,
      });
    }
  }, [hasUnsavedChanges]);

  // --- Tool-output action handlers (invoked by the AI Chatbot via postMessage) ---

  const executeToolOutputsActions = useCallback(async (event: ToolOutputsMessageEvent) => {
    const { data: eventData } = event;

    if ('actions' in eventData) {
      for (const action of eventData.actions) {
        await executeToolOutputsActions({ data: action });
      }

      return;
    }

    if (eventData.action === 'SELECT_PAGE') {
      const { page } = eventData.data;

      skipLoadConditionsRef.current = true;
      try {
        dispatch(setShowFlowBuilder(true));
        FunctionRegistry.removeQueryParamsFromUrl?.();

        const currentState = store.getState() as RootState;

        if (page && page !== currentState.app.activePage) {
          dispatch(setActivePage(page as PageType));
          await dispatch(fetchConditionsAndSlotsAsync()).unwrap().then(async (res: any) => {
            const { conditions } = res;
            const defaultConditionId = conditions?.data?.find((condition: any) => condition.isDefault === true)?.id;

            if (defaultConditionId) {
              dispatch(selectCondition(defaultConditionId));
              const res = await dispatch(selectConditionAsync(defaultConditionId)).unwrap();

              if (res?.data) {
                dispatch(
                  updateBreadcrumbValue({
                    breadCrumb: generateBreadcrumbFormat({ page: res?.data?.location, condition: res?.data?.name }),
                  }),
                );
              }
            }
          });
        }
      } finally {
        skipLoadConditionsRef.current = false;
        sendAckIfNeeded('SELECT_PAGE');
      }

      return;
    }

    if (eventData.action === 'SELECT_CONDITION') {
      const { conditionId } = eventData.data;

      try {
        const currentState = store.getState() as RootState;

        if (conditionId && currentState.conditions.selectedConditionId !== conditionId) {
          dispatch(selectCondition(conditionId));
          const res = await dispatch(selectConditionAsync(conditionId)).unwrap();

          if (res?.data) {
            dispatch(
              updateBreadcrumbValue({
                breadCrumb: generateBreadcrumbFormat({ page: res?.data?.location, condition: res?.data?.name }),
              }),
            );
          }
        }
      } finally {
        sendAckIfNeeded('SELECT_CONDITION');
      }

      return;
    }

    if (eventData.action === 'SELECT_SLOT') {
      const { slotId } = eventData.data;

      try {
        const currentState = store.getState() as RootState;
        const currentBreadcrumb = currentState.app.breadcrumbValue;
        const updatedSlot = currentState.slots.slots.find((slot) => slot.id === slotId);
        const splitBreadcrumb = currentBreadcrumb?.split(' > ') ?? [];
        const page = currentState.app.activePage;
        const condition = splitBreadcrumb[1] ?? '';

        const variant = currentState.conditions.conditions.find((condition) => condition.id === currentState.conditions.selectedConditionId)?.variants?.find((variant: any) => variant.id === (updatedSlot as any)?.variantId);

        if ((updatedSlot as any)?.variantId) {
          dispatch(setSelectedVariantId((updatedSlot as any)?.variantId));
          dispatch(
            updateBreadcrumbValue({
              breadCrumb: generateBreadcrumbFormat({ page, condition, slot: updatedSlot?.name, variant: variant?.name }),
            }),
          );
        }
      } finally {
        sendAckIfNeeded('SELECT_SLOT');
      }

      return;
    }

    if (eventData.action === 'SELECT_VARIANT') {
      const currentState = store.getState() as RootState;
      const currentBreadcrumb = currentState.app.breadcrumbValue;
      const splitBreadcrumb = currentBreadcrumb?.split(' > ') ?? [];
      const page = splitBreadcrumb[0] ?? '';
      const condition = splitBreadcrumb[1] ?? '';
      const slot = splitBreadcrumb[2] ?? '';
      const { variantId } = eventData.data;

      try {
        if (variantId) {
          const selectedCondition = currentState.conditions.conditions.find((condition) => condition.id === currentState.conditions.selectedConditionId);
          const updatedVariant = selectedCondition?.variants?.find((variant: any) => variant.id === variantId);

          dispatch(setSelectedVariantId(variantId));
          dispatch(
            updateBreadcrumbValue({
              breadCrumb: generateBreadcrumbFormat({ page, condition, slot, variant: updatedVariant?.name }),
            }),
          );
        }
      } finally {
        sendAckIfNeeded('SELECT_VARIANT');
      }

      return;
    }

    if (eventData.action === 'OPEN_WIDGET_EDITOR') {
      const { variantId, conditionId, slotId, page } = eventData.data;

      try {
        const currentState = store.getState() as RootState;
        const platformStoreId = currentState.app.platformStoreId;

        const tokenRes = await getToken(platformStoreId!);
        let link = `${WIDGET_EDITOR_URL}?variant=${variantId}&type=${page}&platformStoreId=${platformStoreId}&token=${tokenRes.response.token}&conditionId=${conditionId}&slotId=${slotId}&language=${i18n.language}`;

        setPageInSession(page);
        setSelectedConditionInSession(conditionId);
        setSelectedVariantInSession(variantId);

        if (tokenRes.response?.token) {
          dispatch(setIFrameLink(link));
        }
      } catch (error) { }

      return;
    }

    if (eventData.action === 'UPDATE_SLOT_NAME') {
      const { slotId, slotName } = eventData.data;

      const currentState = store.getState() as RootState;
      const selectedSlot = currentState.conditions.selectedSlot;

      if(selectedSlot?.id === slotId) {
        const page = currentState.app.activePage;
        const currentBreadcrumb = currentState.app.breadcrumbValue;
        const splitBreadcrumb = currentBreadcrumb?.split(' > ') ?? [];
        const condition = splitBreadcrumb[1] ?? '';
        const variant = splitBreadcrumb[3] ?? '';

        dispatch(
          updateBreadcrumbValue({
            breadCrumb: generateBreadcrumbFormat({ page: page as PageType, condition, slot: slotName, variant }),
          }),
        );
      }


      dispatch(updateSlot({ id: slotId, name: slotName }));

      return;
    }

    if (eventData.action === 'ADD_SLOT') {
      const { slot, page, conditionId } = eventData.data;
      const currentState = store.getState() as RootState;
      const currentActivePage = currentState.app.activePage;
      const platformStoreId = currentState.app.platformStoreId;

      const id = slot?.id ?? slot?._id;
      const slotNo = slot?.slotNo ?? 0;

      if (id != null) {
        dispatch(
          addSlot({
            slot: { ...slot, id, _id: id, slotNo } as Partial<Slot> & { id: string; slotNo: number },
            activePage: (page as PageType) ?? currentActivePage,
            platformStoreId: platformStoreId ?? "",
          }),
        );
      }

      const variants = Array.isArray(slot?.variants) ? slot.variants : [];

      for (const variant of variants) {
        const variantId = variant?.id ?? (variant as { _id?: string })?._id;

        if (variantId) {
          try {
            const fetchedVariant = await getVariantById(variantId);

            dispatch(
              addVariant({
                conditionId,
                variant: { ...fetchedVariant, id: fetchedVariant.id ?? variantId },
              }),
            );
          } catch {
            dispatch(
              addVariant({
                conditionId,
                variant: { ...variant, id: variantId },
              }),
            );
          }
        }
      }

      return;
    }

    if (eventData.action === 'DELETE_SLOT') {
      const { page, slotNo } = eventData.data;
      const currentState = store.getState() as RootState;
      const currentActivePage = currentState.app.activePage;
      const platformStoreId = currentState.app.platformStoreId;

      const currentSelectedSlot = currentState.conditions.selectedSlot

      dispatch(
        removeSlot({
          slotNo,
          activePage: (page as PageType) ?? currentActivePage,
          platformStoreId: platformStoreId ?? "",
        }),
      );

      if(currentSelectedSlot?.slotNo === slotNo) {
        const currentBreadcrumb = currentState.app.breadcrumbValue;
        const splitBreadcrumb = currentBreadcrumb?.split(' > ') ?? [];
        const page = currentState.app.activePage;
        const condition = splitBreadcrumb[1] ?? '';

        dispatch(
          updateBreadcrumbValue({
            breadCrumb: generateBreadcrumbFormat({ page: page as PageType, condition}),
          }),
        );

        dispatch(setSelectedSlot(null as any));
        dispatch(setSelectedVariantId(null as any));
      }

      return;
    }

    if (eventData.action === 'UPDATE_SLOT_STATUS') {
      const { active, slotId, conditionId } = eventData.data;

      dispatch(updateVariantStatus({ active, slotId, conditionId }));

      return;
    }

    if (eventData.action === 'MISSING_ENTITIES') {
      const missingEntities = Array.isArray(eventData.data.missingEntities)
        ? eventData.data.missingEntities
        : [];

      const needsProductOrCollection = missingEntities.some(
        (e) => e === 'product_id' || e === 'product_ids' || e === 'collection_id'
      );

      if (needsProductOrCollection) {
        dispatch(openMissingEntitiesModal({ missingEntities }));
      }
    }

    if (eventData.action === 'OPEN_SUPPORT_CHAT') {
      const title = eventData.data.title ?? "Support request";
      const description = eventData.data.description ?? "";

      openCrispChat();
      setTimeout(() => {
        sendSupportMessage(title, description);
      }, 300);
    }
  }, [dispatch]);

  const executeToolOutputsActionsRef = useRef(executeToolOutputsActions);

  executeToolOutputsActionsRef.current = executeToolOutputsActions;

  // --- Public handler: gates tool-output actions behind the unsaved-changes modal ---

  const processToolOutputsActions = useCallback(async (event: ToolOutputsMessageEvent) => {
    preventActionRef.current(() => executeToolOutputsActionsRef.current(event));
  }, []);

  const processToolOutputsActionsRef = useRef(processToolOutputsActions);

  return {
    skipLoadConditionsRef,
    processToolOutputsActionsRef,
  };
};

export default useAIChatContext;
